import React, { useCallback } from "react";
import { Button, Space, Card, Row } from "antd";

import RadioButtonGroup from "../../../common/RadioButtonGroup";
import {
  NextVatDeclarationStatus,
  NextVatDeclarationStatusLabel,
} from "../../../../types";
import VatPaymentFrequencyFilter from "../../../common/TransactionFilters/VatPaymentFrequencyFilter";
import PlanFilter from "../../../common/TransactionFilters/PlanFilter";
import { PaymentFrequency, KontaxPlan } from "../../../../types";
import FilterBoxCard from "../../../common/Card/FilterBoxCard";
import CustomerSearchInput from "@/lib/customerSearch/CustomerSearchInput";

export interface UserFilterOptions {
  email: string;
  status?: NextVatDeclarationStatus;
  vatPaymentFrequency?: PaymentFrequency;
  plans?: Array<KontaxPlan>;
  assigneeId?: string | "unassigned";
}

const UserFilters = ({
  filterOptions,
  setFilterOptions,
  loadAll,
}: {
  filterOptions: UserFilterOptions;
  setFilterOptions: (filterOptions: Partial<UserFilterOptions>) => void;
  loadAll: () => void;
}) => {
  const { email, status, vatPaymentFrequency, plans } = filterOptions;
  const onChangeHandler = useCallback(
    (updatedFilterOptions: Partial<UserFilterOptions>) => {
      setFilterOptions({
        ...filterOptions,
        ...updatedFilterOptions,
        ...(!updatedFilterOptions.email && { email: "" }),
      });
    },
    [filterOptions, setFilterOptions]
  );

  return (
    <Card bordered={false} bodyStyle={{ padding: "0" }}>
      <Row>
        <FilterBoxCard title="Email">
          <Space>
            {/* The box follows `email` when it changes from outside (URL
                seed, Load all), so the VAT view stays in step with ZM.
                SMT-1216/SMT-1354. It resolves Public ID / Account ID to
                the customer's email first (SMT-1396). */}
            <CustomerSearchInput
              id="email"
              value={email}
              onSelect={(selected) => {
                onChangeHandler({ email: selected || "" });
              }}
              style={{ width: "auto" }}
            />

            <Button
              type="default"
              onClick={(event) => {
                event.preventDefault();
                loadAll();
              }}
            >
              Load All
            </Button>
          </Space>
        </FilterBoxCard>
        <FilterBoxCard title="Declaration status">
          <RadioButtonGroup
            value={status}
            isReset={status === undefined}
            options={[
              { label: "All", value: undefined },
              {
                label: NextVatDeclarationStatusLabel.PENDING,
                value: NextVatDeclarationStatus.PENDING,
              },
              {
                label: NextVatDeclarationStatusLabel.READY_TO_CHECK,
                value: NextVatDeclarationStatus.READY_TO_CHECK,
              },
              {
                label: NextVatDeclarationStatusLabel.READY_TO_SEND,
                value: NextVatDeclarationStatus.READY_TO_SEND,
              },
              {
                label: NextVatDeclarationStatusLabel.BLOCKED,
                value: NextVatDeclarationStatus.BLOCKED,
              },
              {
                label: NextVatDeclarationStatusLabel.READY_TO_BOOK,
                value: NextVatDeclarationStatus.READY_TO_BOOK,
              },
            ]}
            onChange={(value: NextVatDeclarationStatus) => {
              onChangeHandler({ status: value });
            }}
          />
        </FilterBoxCard>
        <VatPaymentFrequencyFilter
          fetchAll
          onChangeHandler={onChangeHandler}
          value={vatPaymentFrequency}
        />
        <PlanFilter onChangeHandler={onChangeHandler} value={plans} />
      </Row>
    </Card>
  );
};

export default UserFilters;
